import { useState, useEffect } from 'react';
import AnimatedSection from '@/components/ui/AnimatedSection';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, Image as ImageIcon, Download } from 'lucide-react';
import { LOCAL_ALBUMS } from '@/data/albums';

export default function Photos() {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [albumPhotos, setAlbumPhotos] = useState([]);
    const [loadingPhotos, setLoadingPhotos] = useState(false);

    useEffect(() => {
        fetchAlbums();
    }, []);

    const fetchAlbums = async () => {
        try {
            const { data, error } = await supabase
                .from('albums')
                .select('*, photos(count)')
                .order('date', { ascending: false });

            if (error) throw error;
            
            // Format Supabase albums to match local data structure
            const dbAlbums = (data || []).map(album => ({
              ...album,
              coverImage: album.cover_image_url,
              photoCount: album.photos[0]?.count || 0,
              source: 'supabase'
            }));

            // Format local albums
            const local = LOCAL_ALBUMS.map(album => ({
              ...album,
              photoCount: album.photos.length,
              source: 'local'
            }));

            // Combine and sort by date
            const combined = [...local, ...dbAlbums].sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            );

            setAlbums(combined);
        } catch (error) {
            console.error('Error fetching albums:', error);
            // Fallback to local only if DB fails
            setAlbums(LOCAL_ALBUMS.map(a => ({ ...a, photoCount: a.photos.length, source: 'local' })));
        } finally {
            setLoading(false);
        }
    };

    const handleAlbumClick = async (album) => {
        setSelectedAlbum(album);
        
        if (album.source === 'local') {
          setAlbumPhotos(album.photos);
          return;
        }

        setLoadingPhotos(true);
        try {
            const { data, error } = await supabase
                .from('photos')
                .select('*')
                .eq('album_id', album.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAlbumPhotos(data || []);
        } catch (error) {
            console.error('Error fetching photos:', error);
        } finally {
            setLoadingPhotos(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-36 pb-20">
            <AnimatedSection className="container mx-auto px-6 mb-16 text-center">
                <h1 className="text-4xl md:text-6xl font-serif font-bold text-primary-900 mb-6">
                    Galerie <span className="text-accent-500">Photos</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Retour en images sur nos dernières activités, conférences et excursions.
                </p>
            </AnimatedSection>

            <div className="container mx-auto px-6">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : albums.length === 0 ? (
                    <div className="text-center text-gray-500 py-12">
                        <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                        <p>Aucun album photo pour le moment.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {albums.map((album, index) => (
                            <AnimatedSection key={album.id} delay={index * 0.1}>
                                <motion.div
                                    whileHover={{ y: -5 }}
                                    onClick={() => handleAlbumClick(album)}
                                    className="group relative bg-white rounded-xl overflow-hidden shadow-lg cursor-pointer h-full flex flex-col"
                                >
                                    <div className="aspect-[4/3] bg-gray-200 overflow-hidden relative">
                                        {album.coverImage ? (
                                            <img
                                                src={album.coverImage}
                                                alt={album.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <ImageIcon size={48} />
                                            </div>
                                        )}
                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex justify-between items-end">
                                            <span className="text-white font-medium">Voir les photos</span>
                                            <span className="text-white text-sm bg-black/30 px-2 py-1 rounded-full">
                                                {album.photoCount}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col">
                                        <h3 className="font-bold text-xl text-primary-900 mb-2">{album.title}</h3>
                                        {album.description && (
                                            <p className="text-gray-600 text-sm line-clamp-2 flex-1">{album.description}</p>
                                        )}
                                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                                            <span>{format(new Date(album.date), 'd MMMM yyyy', { locale: fr })}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatedSection>
                        ))}
                    </div>
                )}
            </div>

            {/* Album Detail Modal - Simplified for now */}
            {selectedAlbum && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
                    <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white z-10">
                            <div>
                                <h2 className="text-2xl font-bold text-primary-900">{selectedAlbum.title}</h2>
                                <p className="text-gray-500 text-sm">{format(new Date(selectedAlbum.date), 'd MMMM yyyy', { locale: fr })}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                {selectedAlbum.zipPath && (
                                  <a
                                    href={selectedAlbum.zipPath}
                                    download
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-900 text-white rounded-lg text-sm font-medium hover:bg-primary-800 transition-colors"
                                  >
                                    <Download size={18} />
                                    Télécharger l'album
                                  </a>
                                )}
                                <button
                                    onClick={() => setSelectedAlbum(null)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X size={24} className="text-gray-500" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-50">
                            {loadingPhotos ? (
                                <div className="flex justify-center items-center h-48">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                                </div>
                            ) : albumPhotos.length === 0 ? (
                                <div className="text-center text-gray-500 py-12">
                                    <p>Pas encore de photos dans cet album.</p>
                                </div>
                            ) : (
                                <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                                    {albumPhotos.map((photo) => (
                                        <div key={photo.id} className="break-inside-avoid rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                            <img
                                                src={photo.url}
                                                alt={photo.caption || selectedAlbum.title}
                                                className="w-full h-auto object-cover"
                                                loading="lazy"
                                            />
                                            {photo.caption && (
                                                <div className="bg-white p-2 text-sm text-gray-600">
                                                    {photo.caption}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
